import { useLingui } from '@lingui/react/macro';

import { UserAvatar } from '@/components/UserAvatar/UserAvatar';
import {
  FilterCheckboxItem,
  FilterItemNoResults,
  FilterSearch,
  FiltersList,
  FiltersSubMenu,
} from '@/design_system/Filters/FiltersDrawer/FiltersDrawer';
import IconSupervisor from '@/icons/Supervisor.svg';
import { useInfiniteUsers } from '@/models/user';
import useDebouncedState from '@/utils/useDebouncedState';

import { useFilters } from '../FiltersState';

const MINIMUM_USERS_FOR_SEARCH = 5;
const USERS_PER_PAGE = 20;

export const FiltersSupervisor = () => {
  const { t } = useLingui();
  const {
    supervisors: { supervisors, setSupervisors },
  } = useFilters();
  const [search, debouncedSearch, setSearch] = useDebouncedState<string>('', 300);

  // The first hook is to get the total count of users
  // React-query will only fetch once, even with two hooks (as long as the params are the same)
  const { data: initialData } = useInfiniteUsers({
    permissions: ['supervise_request'],
    limit: USERS_PER_PAGE,
  });
  const { data, fetchNextPage, isLoading } = useInfiniteUsers({
    permissions: ['supervise_request'],
    limit: USERS_PER_PAGE,
    search: debouncedSearch || undefined,
  });

  const users = data?.pages.flatMap((page) => page.users) ?? [];
  const count = initialData?.pages[0]?.meta.count || 0;

  const supervisorsIds = supervisors.map(({ id }) => id);

  const options =
    users.length > 0 ? (
      users.map((user) => (
        <FilterCheckboxItem
          key={user.id}
          id={user.id}
          label={user.name}
          avatar={<UserAvatar user={user} size="x-small" />}
        />
      ))
    ) : (
      <FilterItemNoResults />
    );

  const toggleSupervisor = (userId: string) => {
    const user = users.find((user) => user.id === userId);

    if (user) {
      if (supervisorsIds.includes(userId)) {
        setSupervisors(supervisors.filter((s) => s.id !== userId));
      } else {
        setSupervisors([...supervisors, { id: user.name, name: user.name }]);
      }
    }
  };

  const optionsWithSearch = (
    <FilterSearch
      ariaLabel={t({ id: 'requests.filters.supervisor.label', message: 'Supervisor' })}
      inputProps={{
        ariaLabel: t({
          id: 'requests.filters.supervisor.search.label',
          message: 'Search supervisors',
        }),
        placeholder: t({
          id: 'requests.filters.supervisor.search.label',
          message: 'Search supervisors',
        }),
        value: search,
        onChange: setSearch,
      }}
      onLoadMore={fetchNextPage}
      isLoading={users.length === 0 && isLoading}
      isLoadingMore={isLoading}
      values={supervisorsIds}
      onChange={toggleSupervisor}
    >
      {options}
    </FilterSearch>
  );

  const optionsWithoutSearch = (
    <FiltersList
      ariaLabel={t({ id: 'requests.filters.supervisor.label', message: 'Supervisor' })}
      values={supervisorsIds}
      onChange={toggleSupervisor}
    >
      {options}
    </FiltersList>
  );

  return (
    <FiltersSubMenu
      id="supervisor"
      icon={<IconSupervisor />}
      label={t({ id: 'requests.filters.supervisor.label', message: 'Supervisor' })}
      count={supervisors.length}
    >
      {count < MINIMUM_USERS_FOR_SEARCH ? optionsWithoutSearch : optionsWithSearch}
    </FiltersSubMenu>
  );
};
